import styles from 'css/src/components/public-pages/EnterprisePage.module.scss';

import React, { useState } from 'react';

import EnterpriseTextSection from './sections/EnterpriseTextSection';
import TemplatePreviewsSection, { enterpriseTemplatePreviews } from './TemplatePreviews';

import LogoImages from 'js/src/components/common/LogoImages';
import { BUTTON_SIZES, PrimaryButton } from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import CompanyInfoSection from 'js/src/components/public-pages/common-sections/CompanyInfoSection';
import VideoPlayer from 'js/src/components/public-pages/common-sections/VideoPlayer';
import VideoPlayerWithButton from 'js/src/components/public-pages/common-sections/VideoPlayerWithButton';
import { customVideos, logoImagesLight } from 'js/src/components/public-pages/homepage-data';
import { DEMO_URL } from 'js/src/libs/consts';

const EnterprisePage = React.memo(function EnterprisePage() {
    const [showDemoVideo, setShowDemoVideo] = useState(false);
    const [demoVideoDetails, setDemoVideoDetails] = useState<{
        poster: string;
        aspectRatio: string;
        modalSize: MODAL_SIZES;
        src: string;
    } | null>(null);

    const playDemoVideoInModal = (demoVideoDetails: {
        poster: string;
        aspectRatio: string;
        modalSize: MODAL_SIZES;
        src: string;
    }) => {
        setDemoVideoDetails(demoVideoDetails);
        setShowDemoVideo(true);
    };

    return (
        <div className={`${styles['enterprise-page-container']} mt-l5-0 overflow-x-hidden`}>
            {/* Section 1 */}
            <div className={`p-l5-16 p-l5-sm-48 ${styles['section-1']}`}>
                <img
                    className={styles['section-1__bg']}
                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/2025-website-assets_landing_Gradient%20Backgrounds-03-1280.webp"
                    srcSet="
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/2025-website-assets_landing_Gradient%20Backgrounds-03-320.webp 320w,
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/2025-website-assets_landing_Gradient%20Backgrounds-03-640.webp 640w,
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/2025-website-assets_landing_Gradient%20Backgrounds-03-960.webp 960w,
                        https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/2025-website-assets_landing_Gradient%20Backgrounds-03-1280.webp 1280w,
                    "
                    alt="gradient background"
                    loading="eager"
                    fetchPriority="high"
                />
                <div className="container">
                    <div className="row align-items-center">
                        {/* section 1.1 */}
                        <div className="col-md-8">
                            <div
                                className={`d-flex flex-column my-l5-48 pt-l5-48 text-start ${styles['hero-title']}`}
                            >
                                <h1>
                                    Fast, consistent
                                    <br />
                                    <span className={styles['title-highlight']}>
                                        {' '}
                                        video creation{' '}
                                    </span>
                                    for
                                    <br />
                                    every team, everywhere
                                </h1>
                                <h4
                                    className={`mt-l5-16 pt-l5-16 px-l5-0 ${styles['hero-subtitle']}`}
                                >
                                    Produce high-quality, on-brand videos across global teams
                                    without adding headcount. Save money, save time, and get results
                                    with Lumen5 for Enterprise.
                                </h4>

                                <div className="mt-l5-48">
                                    <PrimaryButton
                                        size={BUTTON_SIZES.LG}
                                        text="Request a demo"
                                        href={DEMO_URL}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <TemplatePreviewsSection rows={2} templatePreviews={enterpriseTemplatePreviews} />

                {/* Trusted by global brands section */}
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        <div className="col-md-12">
                            <div className="d-flex py-l5-32">
                                <h4 className="fw-semibold logo-header color-white">
                                    Trusted by global brands
                                </h4>
                            </div>
                        </div>
                    </div>
                </div>
                <LogoImages logos={logoImagesLight} />
            </div>

            <div
                className={`p-l5-16 p-l5-sm-48 section-2 ${styles['section-primary-core']} pb-l5-48 text-white`}
            >
                <div className="container">
                    <div className="justify-content-center align-items-center">
                        <div className="d-flex flex-column align-items-start my-l5-48">
                            <h2>Keep every video on-brand</h2>
                            <p
                                className={`font-l5 font-l5--regular my-l5-16 px-l5-0 w-50 ${styles['mobile-full']}`}
                            >
                                When your videos look like your brand, they work like your brand. We
                                build your brand environment right into the platform so every team
                                member can create content that feels uniquely yours.
                            </p>
                        </div>
                        <div className={`${styles['border-video-container']} mb-l5-64`}>
                            <video
                                className="w-100"
                                playsInline
                                disablePictureInPicture
                                disableRemotePlayback
                                controls
                                preload="none"
                                aria-label="Explainer video - How Lumen5 helps your team create on-brand videos at scale."
                                poster="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/videos/BrandedTemplateExplainerThumbnail.webp"
                            >
                                <source
                                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/videos/BrandedTemplateExplainer.mp4"
                                    type="video/mp4"
                                />
                                <source
                                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/videos/BrandedTemplateExplainer.webm"
                                    type="video/webm"
                                />
                            </video>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 3  Turn Content Into.... */}
            <div className={`p-l5-16 p-l5-sm-48 ${styles['section-3']}`}>
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        {/* section 3.1 */}
                        <div className="col-md-6 col-sm-12 order-md-2 order-sm-1">
                            <div className="d-flex justify-content-center">
                                <div className="d-flex flex-column align-items-start my-l5-48">
                                    <div className="d-flex flex-column my-l5-16">
                                        <h3>
                                            Create content on budget and on time by bringing video
                                            in-house
                                        </h3>
                                    </div>

                                    <h5 className="my-l5-16 px-l5-0">
                                        <strong>
                                            Unleash team creativity with seamless video creation.
                                        </strong>{' '}
                                        Embrace the power of collaborative video production with
                                        Lumen5 Enterprise. Empower your team to create compelling
                                        videos on demand, breaking free from constraints of
                                        traditional workflows.
                                    </h5>

                                    <h5 className="my-l5-16 px-l5-0 w-75">
                                        <PrimaryButton
                                            size={BUTTON_SIZES.MD}
                                            text="Request a demo"
                                            href={DEMO_URL}
                                        />
                                    </h5>

                                    <div className="font-l5 font-l5--regular my-l5-16 px-l5-0 w-75 fst-italic">
                                        “With an agency, the turnaround time is usually one week.
                                        But with Lumen5, it’s cut down from weeks to hours.”
                                    </div>
                                    <div>
                                        <strong>Michelle Hsiao</strong>
                                        <br />
                                        <span>Visual content director</span>
                                    </div>
                                    <div>
                                        <img
                                            loading="lazy"
                                            className={styles['company-logo']}
                                            src="https://cdn.lumen5.com/lumen5-site-images/website-assets/logo-siemens-color1.png"
                                            alt="Siemens logo"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* section 3.2 */}
                        <div className="col-md-6 col-sm-12 order-md-1 order-sm-2">
                            <div className="d-flex container justify-content-sm-start justify-content-md-center align-items-center mt-l5-48 pt-l5-48">
                                <video
                                    className={`w-100 ${styles['banner-img']}`}
                                    autoPlay={true}
                                    muted
                                    loop
                                    playsInline
                                    disablePictureInPicture
                                    disableRemotePlayback
                                    preload="none"
                                    poster="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-budget-poster.webp"
                                >
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-budget.webm"
                                        type="video/webm"
                                    />
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-budget.mp4"
                                        type="video/mp4"
                                    />
                                </video>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 4  Integrate. high quality... */}
            <div className={`p-l5-16 p-l5-sm-48 ${styles['section-4']}`}>
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        {/* section 4.1 */}
                        <div className="col-md-6 col-sm-6 col-sm-12">
                            <div className="d-flex justify-content-center">
                                <div className="d-flex flex-column align-items-start my-l5-48">
                                    <div className="d-flex flex-column my-l5-16">
                                        <h3>
                                            Expand your reach globally with multilingual
                                            Content{' '}
                                        </h3>
                                    </div>

                                    <h5 className="my-l5-16 px-l5-0">
                                        Lumen5 is your passport to multilingual content creation,
                                        complete with translation tools for seamless expansion into
                                        new markets. Take advantage of our translation options and
                                        select AI Voices that are available in multiple languages.
                                    </h5>

                                    <h5 className="my-l5-16 px-l5-0 w-75">
                                        <PrimaryButton
                                            size={BUTTON_SIZES.MD}
                                            text="Request a demo"
                                            href={DEMO_URL}
                                        />
                                    </h5>

                                    <div className="font-l5 font-l5--regular my-l5-16 px-l5-0 w-75 fst-italic">
                                        “Our team is international and we’re able to create Lumen5
                                        videos for global that need to be regionalized into
                                        different languages.”
                                    </div>
                                    <div>
                                        <strong>Eric Chu</strong>
                                        <br />
                                        <span>Social and digital content manager</span>
                                    </div>
                                    <div>
                                        <img
                                            loading="lazy"
                                            className={styles['company-logo']}
                                            src="https://cdn.lumen5.com/lumen5-site-images/website-assets/logo-cisco-color.png"
                                            alt="Cisco logo"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* section 4.2 */}
                        <div className="col-md-5 offset-md-1 col-sm-12">
                            <div className="d-flex justify-content-sm-start justify-content-md-center align-items-center mt-l5-48 pt-l5-48">
                                <video
                                    className={`w-100 ${styles['banner-img']}`}
                                    autoPlay={true}
                                    muted
                                    loop
                                    playsInline
                                    disablePictureInPicture
                                    disableRemotePlayback
                                    preload="none"
                                    poster="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-translate-poster.webp"
                                >
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-translate-v2.webm"
                                        type="video/webm"
                                    />
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-translate.mp4"
                                        type="video/mp4"
                                    />
                                </video>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 4.5 */}
            <div className={`p-l5-16 p-l5-sm-48 ${styles['section-4']}`}>
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        {/* section 3.1 */}
                        <div className="col-md-12 col-sm-12 order-md-2 order-sm-1">
                            <div className="d-flex justify-content-center">
                                <div className="d-flex flex-column align-items-start my-l5-48 text-center">
                                    <div className="d-flex flex-column my-l5-16">
                                        <h3>Maximize value with cutting-edge AI features</h3>
                                        <h5 className="my-l5-16 px-l5-0">
                                            Experience the true potential of AI-driven video
                                            creation with Lumen5 Enterprise. Leverage Idea to Video
                                            and our AI Script Composer to transform ideas and
                                            thoughts into captivating videos. Incorporate custom and
                                            cloned AI voices to relate and engage better with your
                                            audience. Elevate your brand’s storytelling with
                                            innovation at your fingertips.
                                        </h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 5 Customization with just... */}
            <div className={`p-l5-16 p-l5-sm-48 mb-l5-48 ${styles['section-5']}`}>
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        {/* section 5.1 */}

                        <div className="col-md-6 col-sm-12 order-md-2 order-sm-1">
                            <div className="d-flex justify-content-center">
                                <div className="d-flex flex-column align-items-start my-l5-48">
                                    <div className="d-flex flex-column my-l5-16">
                                        <h3>
                                            Craft customized brand templates aligned with
                                            your&nbsp;brand
                                        </h3>
                                    </div>

                                    <h5 className="my-l5-16 px-l5-0">
                                        Maintain complete brand control with Lumen5’s custom video
                                        templates
                                        <br />
                                        <br />
                                        We work with your brand team to handcraft a unique custom
                                        template following the exact details of your brand
                                        guidelines.
                                    </h5>

                                    <h5 className="my-l5-16 px-l5-0 w-75">
                                        <PrimaryButton
                                            size={BUTTON_SIZES.MD}
                                            text="Request a demo"
                                            href={DEMO_URL}
                                        />
                                    </h5>

                                    <div className="font-l5 font-l5--regular my-l5-16 px-l5-0 w-75 fst-italic">
                                        &quot;We make sure that the social content we create aligns
                                        to brand guidelines, and Lumen5 makes it easy for us to
                                        enhance the look and feel of our content promotion.&quot;
                                    </div>
                                    <div>
                                        <strong>Eric Chu</strong>
                                        <br />
                                        <span>Social and digital content manager</span>
                                    </div>
                                    <div>
                                        <img
                                            loading="lazy"
                                            className={styles['company-logo']}
                                            src="https://cdn.lumen5.com/lumen5-site-images/website-assets/logo-cisco-color.png"
                                            alt="Cisco logo"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* section 5.2 */}
                        <div className="col-md-6 col-sm-12 order-md-1 order-sm-2">
                            <div className="d-flex justify-content-sm-start justify-content-md-center align-items-center px-l5-48">
                                <video
                                    className={`w-100 ${styles['banner-img']}`}
                                    autoPlay={true}
                                    muted
                                    loop
                                    playsInline
                                    disablePictureInPicture
                                    disableRemotePlayback
                                    preload="none"
                                    poster="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-color-poster.webp"
                                >
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-color.webm"
                                        type="video/webm"
                                    />
                                    <source
                                        src="https://cdn.lumen5.com/lumen5-site-images/website-assets/enterprise-color.mp4"
                                        type="video/mp4"
                                    />
                                </video>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 5.5 */}
            <div className={`p-l5-16 p-l5-sm-48 ${styles['section-4']}`}>
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        {/* section 3.1 */}
                        <div className="col-md-12 col-sm-12 order-md-2 order-sm-1">
                            <div className="d-flex justify-content-center">
                                <div className="d-flex flex-column align-items-start my-l5-48 text-center">
                                    <div className="d-flex flex-column my-l5-16">
                                        <h3>Stay on brand from beginning to end</h3>
                                        <h5 className="my-l5-16 px-l5-0">
                                            We work with your brand team every step of the way,
                                            ensuring your video creation process is seamless with
                                            backend services tailored to your needs. Streamline your
                                            workflow with SSO (Single Sign-On) configurations,
                                            providing secure access for multiple team members.
                                            Integrate with our DAM (Digital Asset Management)
                                            integration effortlessly, ensuring instant access to
                                            your vast and approved media library.
                                        </h5>
                                        <br />
                                        <h5>
                                            <PrimaryButton
                                                size={BUTTON_SIZES.MD}
                                                text="Request a demo"
                                                href={DEMO_URL}
                                            />
                                        </h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 6: Captivating Videos  */}
            <div className={`p-l5-16 p-l5-sm-48 ${styles['section-template']}`}>
                <div className="container">
                    <div className="row justify-content-center align-items-center">
                        <div className="col-md-8">
                            <div className="d-flex flex-column my-l5-16 align-items-center text-center">
                                <h3 className="text-center">Bespoke brand templates</h3>
                            </div>
                            <h5 className="my-l5-16 px-l5-0 text-center">
                                We work with your brand team every step of the way
                            </h5>
                        </div>
                    </div>
                    <div className="row">
                        {customVideos.map((video) => (
                            <VideoPlayerWithButton
                                key={`captivating-videos-${video.src}`}
                                className="col-md-6"
                                imgSrc={video.poster}
                                videoSrc={video.src}
                                onClick={() => playDemoVideoInModal(video)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Smart Features... */}
            <div className="py-l5-64 p-l5-sm-48 text-white">
                <div className="container py-l5-64">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <div className="d-flex flex-column my-l5-16 align-items-center">
                                <h2>Smart features that work the way your team does</h2>
                            </div>
                            <p className="font-l5 font-l5--regular my-l5-16 px-l5-0">
                                SSO, DAM, and multilingual magic—oh my! From seamless single sign-on
                                and direct integrations with tools like Frontify and Bynder, to
                                smarter workflows with permissions, approvals, and AI-assisted video
                                creation, everything’s built to simplify your process and empower
                                your team. No clutter, no friction, just the features you need,
                                right where you need them.
                            </p>
                        </div>
                        <div className="col-md-12">
                            <div className="d-flex flex-wrap">
                                <div
                                    className={`flex-fill p-l5-32 ${styles['smart-feature']} text-center`}
                                >
                                    <img
                                        src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/enterprise/SmartFeatures/translations.webp"
                                        alt="Translations"
                                        className="w-100"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <p className="font-l5 font-l5--regular my-l5-16 px-l5-0 text-center">
                                        Translations
                                    </p>
                                </div>
                                <div
                                    className={`flex-fill p-l5-32 ${styles['smart-feature']} text-center`}
                                >
                                    <img
                                        src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/enterprise/SmartFeatures/digital_asset_management.webp"
                                        alt="Digital asset management"
                                        className="w-100"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <p className="font-l5 font-l5--regular my-l5-16 px-l5-0 text-center">
                                        Digital asset management
                                    </p>
                                </div>
                                <div
                                    className={`flex-fill p-l5-32 ${styles['smart-feature']} text-center`}
                                >
                                    <img
                                        src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/enterprise/SmartFeatures/single_sign_on.webp"
                                        alt="Single sign-on"
                                        className="w-100"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <p className="font-l5 font-l5--regular my-l5-16 px-l5-0 text-center">
                                        Single sign-on
                                    </p>
                                </div>
                                <div
                                    className={`flex-fill p-l5-32 ${styles['smart-feature']} text-center`}
                                >
                                    <img
                                        src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/enterprise/SmartFeatures/ai_voiceover.webp"
                                        alt="AI voiceover"
                                        className="w-100"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <p className="font-l5 font-l5--regular my-l5-16 px-l5-0 text-center">
                                        AI voiceover
                                    </p>
                                </div>
                                <div
                                    className={`flex-fill p-l5-32 ${styles['smart-feature']} text-center`}
                                >
                                    <img
                                        src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/enterprise/SmartFeatures/talking_head.webp"
                                        alt="Talking head"
                                        className="w-100"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <p className="font-l5 font-l5--regular my-l5-16 px-l5-0 text-center">
                                        Talking head
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <EnterpriseTextSection />

            {/* Case studies / Testimonials */}
            <div className={`p-l5-16 p-l5-sm-48 ${styles['testimonials-section']} text-white`}>
                <img
                    className={styles['testimonials-section__bg']}
                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/enterprise/testimonials_dark_gradient.webp"
                    alt="background gradient"
                    loading="lazy"
                    decoding="async"
                />
                <div className="container">
                    {/* section 10.1 */}
                    <div className="row">
                        <div className="col-md-8">
                            <div className="d-flex flex-column mb-l5-32 mt-l5-64">
                                <h2>
                                    From views to revenues:
                                    <br />
                                    See what video can really do
                                </h2>
                            </div>
                            <p className="font-l5 font-l5--regular my-l5-16 px-l5-0">
                                Video isn’t just for reach. It’s for results. Lumen5 helps you turn
                                passive views into real business outcomes with videos built for
                                engagement, conversion, and scale. Still curious? See how other
                                teams are using Lumen5 to turn attention into growth.
                            </p>
                        </div>
                    </div>
                    {/* section 10.2 */}
                    <CompanyInfoSection />
                </div>
            </div>

            {/* Your next 100 videos... */}
            <div
                className={`p-l5-16 p-l5-sm-48 mb-l5-48 text-white ${styles['lumen5-shape-background']}`}
            >
                <img
                    className={styles['lumen5-shape-background-img']}
                    src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/landing/shapes/bottom_shapes.webp"
                    alt="background shapes"
                    loading="lazy"
                    decoding="async"
                />
                <div className="container align-items-start">
                    <div className={styles['lumen5-shape-background-content']}>
                        <div className="d-flex flex-column align-items-start my-l5-16">
                            <h2>
                                Your next 100 videos can feel on brand, on point, and on purpose
                            </h2>
                        </div>
                        <p className="font-l5 font-l5--regular my-l5-16 px-l5-0">
                            Your content should do more than just get seen. With Lumen5, every video
                            helps move your audience forward and turns momentum into measurable
                            success.
                        </p>
                        <div className="d-flex align-items-center my-l5-48">
                            <PrimaryButton
                                size={BUTTON_SIZES.LG}
                                text="Talk to an expert"
                                href={DEMO_URL}
                            />

                            <div className="font-l5 font-l5--regular my-l5-16 ms-l5-32">
                                <a href="/features/" className="text-white">
                                    Explore features →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Logo section */}
            <div className="p-l5-16 mt-l5-64">
                <LogoImages logos={logoImagesLight} rows={2} />
            </div>

            {/* Video Player Modal */}
            <Modal
                showing={showDemoVideo}
                onHidden={() => setShowDemoVideo(false)}
                closeable={false}
                size={demoVideoDetails ? demoVideoDetails.modalSize : MODAL_SIZES.XL}
                dialogClassName={'transparent-dialog text-center'}
                backdropClassName={'darker-backdrop'}
            >
                <div className="container-fluid">
                    {demoVideoDetails && (
                        <VideoPlayer
                            posterUrl={demoVideoDetails.poster}
                            videoUrl={demoVideoDetails.src}
                            showControls={true}
                            loop={false}
                            muted={false}
                            autoPlay={true}
                            classNames={`m-l5-4 ${styles['rounded-video']} ${styles['focus-outline-none']} w-100`}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
});

export default EnterprisePage;
